import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { RestService } from 'src/app/core/services/rest.service';
import { EventType, ICommunityPassports, IEventReview, IFavoriteEventMutationRequest, IFavoriteEventMutationResponse, VoteState } from '../models/passport.models';
import { Profile } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class PassportService {
  constructor(private http: RestService) {}

  getData(): Promise<any> {
    const url = environment.apiURL + 'api/passport-profile';
    return this.http.get<any>({
      url,
      headers: new HttpHeaders({})
    }).toPromise();
  }

  updateData(request: any): Promise<any> {
    const url = environment.apiURL + 'api/update-passport-profile';
    return this.http.post<any, any>({
      url,
      body: request,
      headers: new HttpHeaders({})
    }).toPromise();
  }

  updateProfilePic(userId: number, imageName: string, imageFile: File): Promise<any> {
    const url = environment.apiURL + `api/user-profile/${userId}/profilePic`;

    const formData = new FormData();
    formData.append('picture', imageFile);

    return this.http.post<any, any>({
      url,
      body: formData,
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getEvents(): Observable<any> {
    const url = environment.apiURL + 'api/event-for-favourites';
    return this.http.get({
      url,
      headers: new HttpHeaders({})
    });
  }

  getEventPurchaseHistory(): Promise<any> {
    const url = environment.apiURL + 'agiletix/customer/events/purchase/history';
    return this.http.get({
      url,
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getCommunityUserEventPurchaseHistory(userId: number): Promise<any> {
    const url = environment.apiURL + 'agiletix/customer/events/purchase/history';
    return this.http.get({
      url,
      pathVariables: [ `${userId}` ],
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getSeasonsFilters(): Promise<any> {
    const url = environment.apiURL + 'api/passport-seasons';
    return this.http.get({
      url,
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getEventReview(eventId: number, eventType: EventType): Promise<IEventReview> {
    const url = environment.apiURL + `api/event-review`;
    return this.http.get<IEventReview>({
      url,
      pathVariables: [ `${eventId}` ],
      queryParameters: {
        eventType
      },
      headers: new HttpHeaders({})
    }).toPromise();
  }

  saveEventReview(eventId: number, eventReview: IEventReview): Promise<IEventReview> {
    const url = environment.apiURL + `api/event-review`;
    return this.http.post<IEventReview, IEventReview>({
      url,
      pathVariables: [ `${eventId}` ],
      body: eventReview,
      queryParameters: {
        eventType: eventReview.eventType
      },
      headers: new HttpHeaders({
        'content-type': 'application/json'
      })
    }).toPromise();
  }

  updateEventReview(eventReviewId: number, eventReview: IEventReview): Promise<IEventReview> {
    const url = environment.apiURL + `api/event-review`;

    eventReview.id = (eventReview as any).eventReviewId;
    delete (eventReview as any).eventReviewId;

    return this.http.put<IEventReview, IEventReview>({
      url,
      pathVariables: [ `${eventReviewId}` ],
      body: eventReview,
      headers: new HttpHeaders({
        'content-type': 'application/json'
      })
    }).toPromise();
  }

  updateReviewVote(eventReviewId: number, voteState: VoteState): Promise<IEventReview> {
    const url = environment.apiURL + `api/event-review/vote`;
    return this.http.post<any, IEventReview>({
      url,
      pathVariables: [ `${eventReviewId}`, `${voteState}` ],
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getCommunityPassports(section: string): Promise<ICommunityPassports> {
    const url = environment.apiURL + `api/community-passports`;
    return this.http.get<ICommunityPassports>({
      url,
      pathVariables: [ `${section}` ],
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getCommunityPassport(userId: number): Promise<Profile> {
    const url = environment.apiURL + `api/passport-profile`;
    return this.http.get<any>({
      url,
      pathVariables: [ `${userId}` ],
      headers: new HttpHeaders({})
    }).toPromise();
  }

  getCommunityPassportReview(eventId: number, eventType: EventType, userId: number): Promise<IEventReview> {
    const url = environment.apiURL + `api/event-review/community`;
    return this.http.get<IEventReview>({
      url,
      pathVariables: [ `${eventId}`, `${userId}` ],
      queryParameters: {
        eventType
      },
      headers: new HttpHeaders({})
    }).toPromise();
  }

  searchPassports(term: string): Promise<Profile[]> {
    const url = environment.apiURL + 'api/passport-profile/search';
    return this.http.get<Profile[]>({
      url,
      queryParameters: {
        term
      },
      headers: new HttpHeaders({})
    }).toPromise();
  }

  updateFavoriteEventState(favoriteEventMutationRequest: IFavoriteEventMutationRequest): Promise<IFavoriteEventMutationResponse> {
    const url = environment.apiURL + 'api/update-favorite-event';
    return this.http.patch<IFavoriteEventMutationRequest, IFavoriteEventMutationResponse>({
      url,
      headers: new HttpHeaders({}),
      body: favoriteEventMutationRequest
    }).toPromise();
  }
}
